import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const getAnyToken = () => {
  const adminToken = localStorage.getItem("adminToken");
  const token = localStorage.getItem("token");

  let authToken = null;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    authToken = auth?.token || null;
  } catch {}

  return adminToken || token || authToken || "";
};

const OrderSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchOrderSummary = async () => {
      try {
        const jwtToken = getAnyToken();

        if (!jwtToken) {
          navigate("/admin/login");
          return;
        }

        const response = await axios.get(
          `${baseurl}/api/admin/order-summary`,
          {
            headers: { Authorization: `Bearer ${jwtToken}` },
            withCredentials: true,
          }
        );

        if (isMounted) setSummary(response.data);
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/admin/login");
        }
        if (isMounted) setSummary(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOrderSummary();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // =========================
  // ✅ EXCEL DOWNLOAD FUNCTION
  // =========================
  const downloadExcel = () => {
    if (!summary) return;

    const data = [
      {
        "Pending Orders": summary.totalPendingOrder ?? 0,
        "Confirmed Orders": summary.totalConfirmedOrder ?? 0,
        "Cancelled Orders": summary.totalCanceledOrder ?? 0,
        "Delivered Orders": summary.totalDeliveredOrder ?? 0,
        "Shipped Orders": summary.totalShippedOrder ?? 0,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Order Summary");

    XLSX.writeFile(
      workbook,
      `Order_Summary_${Date.now()}.xlsx`
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl font-semibold text-gray-600">
          Loading Order Summary...
        </p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl font-semibold text-red-500">
          Failed to load order summary
        </p>
      </div>
    );
  }

  const cards = [
    {
      title: "Pending Orders",
      value: summary.totalPendingOrder ?? 0,
      color: "text-yellow-600",
      route: "/orders/pending",
    },
    {
      title: "Confirmed Orders",
      value: summary.totalConfirmedOrder ?? 0,
      color: "text-blue-600",
      route: "/orders/confirmed",
    },
    {
      title: "Cancelled Orders",
      value: summary.totalCanceledOrder ?? 0,
      color: "text-red-600",
      route: "/orders/cancelled",
    },
    {
      title: "Delivered Orders",
      value: summary.totalDeliveredOrder ?? 0,
      color: "text-green-600",
      route: "/orders/delivered",
    },
    {
      title: "Shipped Orders",
      value: summary.totalShippedOrder ?? 0,
      color: "text-purple-600",
      route: "/orders/shipped",
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-gray-800">
          Order Summary
        </h1>

        {/* ✅ DOWNLOAD BUTTON */}
        <button
          onClick={downloadExcel}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          ⬇ Download Excel
        </button>
      </div>

      {/* CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => navigate(card.route)}
            className="bg-white border rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md hover:scale-[1.02] transition"
          >
            <h2 className={`text-lg font-semibold ${card.color}`}>
              {card.title}
            </h2>

            <p className="text-4xl font-bold text-gray-800 mt-3">
              {card.value}
            </p>

            <p className="text-sm text-gray-500 mt-2">
              Click to view details
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderSummary;